import { OdysseyRead } from 'api/schema'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import { toISOStringYYYYMMDD } from 'pages/TaskScheduler/TaskSchedulerUtils'
import { useNavigate } from 'react-router-dom'
import { PATH } from 'utils/constants'
import { IMission } from 'utils/interfaces'
import { isRescheduledOccurrence } from 'utils/odysseyReschedule'
import { getScenarioLaunchBlockReasonTitle } from 'utils/scenarioReadiness'

import style from './FacilityCleaningTaskItem.module.css'

/** Schedule column text for Gausium (GS) mirror rows — local FMS schedule is N/A. */
export const SCHEDULE_PLACEHOLDER_TEXT = 'To be soon'

type CombinedTask = OdysseyRead & {
  isOdyssey?: boolean
  isGausiumMirror?: boolean
  name?: string
  task_name?: string
  map_name?: string
  gausium_map_id?: number | null
  robot_id?: number
}

export interface IFacilityCleaningTaskItemProps {
  task: CombinedTask
  onClick?: () => void
  highlighted?: boolean
  showCheckbox?: boolean
  checked?: boolean
  onCheckboxChange?: (checked: boolean) => void
  scheduledMissionsDetails?: IMission[]
  referencedScenarios?: any[]
  onLaunch?: (task: CombinedTask) => void
  onReschedule?: (task: CombinedTask) => void
  onCancelForToday?: (task: CombinedTask) => void
  isOverlapping?: boolean
  scheduleTextOverride?: string
  disableActions?: boolean
  disableCheckbox?: boolean
  showLaunchOnlyAction?: boolean
  displayOffsetMinutes?: number
}

const FacilityCleaningTaskItem = ({
  task,
  onClick,
  highlighted,
  showCheckbox,
  checked,
  onCheckboxChange,
  scheduledMissionsDetails,
  referencedScenarios,
  onLaunch,
  onReschedule,
  onCancelForToday,
  isOverlapping,
  scheduleTextOverride,
  disableActions,
  disableCheckbox,
  showLaunchOnlyAction,
  displayOffsetMinutes = 0,
}: IFacilityCleaningTaskItemProps) => {
  const browserOffsetMinutes = -new Date().getTimezoneOffset()
  const shiftMs = (displayOffsetMinutes - browserOffsetMinutes) * 60_000

  const navigate = useNavigate()

  const getMissions = () => {
    if (task.isOdyssey) return (task as OdysseyRead).missions || []
    return []
  }

  const getDisplayName = () => {
    if (task.name) return task.name
    return getMissions().join(', ') || '-'
  }

  const missionIds = getMissions()
  const displayName = getDisplayName()
  const taskColumnText = task.isGausiumMirror
    ? String(task.task_name || task.name || '-').trim() || '-'
    : String(task.id)
  const scenarioMissionColumnText = task.isGausiumMirror ? String(task.map_name || '-').trim() || '-' : displayName

  const referencedScenario =
    task.isOdyssey && (task as OdysseyRead).scenario
      ? referencedScenarios?.find(s => s.id === (task as OdysseyRead).scenario)
      : undefined
  const isScenarioInconsistent = Boolean(referencedScenario?.missions_type_inconsistent)
  const isServicePointsIncomplete = Boolean(referencedScenario?.service_points_incomplete)

  const hasUncalculatedMissions = missionIds.some((mId: number) => {
    const details = scheduledMissionsDetails?.find(m => m.mission.id === mId)
    return details && details.status !== 'ok'
  })

  const isLaunchDisabled = task.isGausiumMirror
    ? task.gausium_map_id == null
    : missionIds.length === 0 ||
      isScenarioInconsistent ||
      isServicePointsIncomplete ||
      hasUncalculatedMissions

  const launchBlockTitle = task.isGausiumMirror
    ? task.gausium_map_id == null
      ? 'Gausium map is not linked'
      : undefined
    : getScenarioLaunchBlockReasonTitle({
        missions_statuses:
          missionIds.length === 0
            ? []
            : missionIds.map((mId: number) => {
                const details = scheduledMissionsDetails?.find(m => m.mission.id === mId)
                return details?.status || 'ok'
              }),
        missions_type_inconsistent: isScenarioInconsistent,
        service_points_incomplete: isServicePointsIncomplete,
      })

  const rowTitle = isOverlapping
    ? 'This task overlaps with another schedule for the same robot'
    : launchBlockTitle

  const getSingleScheduleString = (item: CombinedTask) => {
    let schedule = ''
    const days = item.scheduler_settings?.weekdays
    if (days) {
      if (days.SU === 'true') schedule += 'SU '
      if (days.MO === 'true') schedule += 'MO '
      if (days.TU === 'true') schedule += 'TU '
      if (days.WE === 'true') schedule += 'WE '
      if (days.TH === 'true') schedule += 'TH '
      if (days.FR === 'true') schedule += 'FR '
      if (days.SA === 'true') schedule += 'SA '
    }

    if (item.scheduler_settings?.datetime) {
      const originalDt = new Date(item.scheduler_settings.datetime.replace(' ', 'T'))
      const eventBrowserOffsetMinutes = -originalDt.getTimezoneOffset()
      const eventShiftMs = (displayOffsetMinutes - eventBrowserOffsetMinutes) * 60_000
      const dt = new Date(originalDt.getTime() + eventShiftMs)
      const timeStr = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
      schedule += '\n' + timeStr

      const durationSec = (item as any).predicted_duration || (item as any).execution_time
      if (durationSec) {
        const endDt = new Date(dt.getTime() + durationSec * 1000)
        const endTimeStr = endDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
        schedule += ' - ' + endTimeStr
      }
    }

    return schedule.trim() || '-'
  }

  const displaySchedule = scheduleTextOverride ?? getSingleScheduleString(task)
  const isSchedulePlaceholder = Boolean(task.isGausiumMirror && displaySchedule === SCHEDULE_PLACEHOLDER_TEXT)

  const todayStr = toISOStringYYYYMMDD(new Date())
  const isRescheduledToday = isRescheduledOccurrence(task.scheduler_settings, todayStr)
  const isCanceledToday = Boolean(task.scheduler_settings?.except_days?.includes(todayStr) && !isRescheduledToday)

  const isPassedToday = (() => {
    if (!task.scheduler_settings?.datetime) return false
    const schedDateRaw = new Date(task.scheduler_settings.datetime.replace(' ', 'T'))
    const eventBrowserOffsetMinutes = -schedDateRaw.getTimezoneOffset()
    const eventShiftMs = (displayOffsetMinutes - eventBrowserOffsetMinutes) * 60_000
    const schedDate = new Date(schedDateRaw.getTime() + eventShiftMs)
    const nowRaw = new Date()
    const nowBrowserOffsetMinutes = -nowRaw.getTimezoneOffset()
    const nowShiftMs = (displayOffsetMinutes - nowBrowserOffsetMinutes) * 60_000
    const now = new Date(nowRaw.getTime() + nowShiftMs)
    const todaySched = new Date(now)
    todaySched.setHours(schedDate.getHours(), schedDate.getMinutes(), schedDate.getSeconds(), 0)
    return now > todaySched
  })()

  return (
    <div
      className={clsx(style.taskRow, {
        [style.taskRowGausium]: task.isGausiumMirror,
        [style.highlighted]: highlighted,
        [style.errorRow]: isLaunchDisabled,
        [style.overlappingRow]: isOverlapping,
        [style.canceledRow]: isCanceledToday,
        [style.rescheduledRow]: isRescheduledToday,
      })}
      title={rowTitle}
      onClick={e => {
        e.stopPropagation()
        onClick?.()
      }}
    >
      <div></div>
      <span className={style.colLeft} title={taskColumnText}>
        {taskColumnText}
      </span>
      <span className={style.colMultiline} title={scenarioMissionColumnText}>
        {scenarioMissionColumnText}
      </span>
      <span
        className={clsx(style.colSchedule, isSchedulePlaceholder && style.schedulePlaceholder)}
        title={displaySchedule}
      >
        {displaySchedule}
        {isCanceledToday && <div className={style.canceledTag}>Canceled for today</div>}
        {isRescheduledToday && <div className={style.rescheduledTag}>Rescheduled for today</div>}
      </span>
      <span className={style.colCenter} onClick={showCheckbox ? e => e.stopPropagation() : undefined}>
        {showCheckbox && (
          <input
            type="checkbox"
            checked={checked}
            disabled={disableCheckbox}
            onChange={e => onCheckboxChange?.(e.target.checked)}
          />
        )}
      </span>
      <span className={style.colCenter}>
        {showLaunchOnlyAction && (
          <ActionsMenu>
            <ActionsMenuItem onClick={() => onLaunch?.(task)} disabled={isLaunchDisabled} title={launchBlockTitle}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Launch</span>
            </ActionsMenuItem>
          </ActionsMenu>
        )}
        {!showLaunchOnlyAction && !disableActions && missionIds.length > 0 && (
          <ActionsMenu>
            <ActionsMenuItem onClick={() => onLaunch?.(task)} disabled={isLaunchDisabled} title={launchBlockTitle}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Launch</span>
            </ActionsMenuItem>
            <ActionsMenuItem
              onClick={() => {
                navigate(`${PATH.ANALYTICS}?taskId=${task.id}`)
              }}
            >
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Analytics</span>
            </ActionsMenuItem>
            <ActionsMenuItem
              onClick={() => {
                const path =
                  task.isOdyssey && (task as OdysseyRead).scenario
                    ? `${PATH.SCENARIO_ID.replace(':id', String((task as OdysseyRead).scenario))}`
                    : `${PATH.MISSION}${missionIds[0]}`
                navigate(path)
              }}
            >
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Edit</span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={() => onReschedule?.(task)}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText}>Reschedule</span>
            </ActionsMenuItem>
            {task.scheduler_settings?.weekdays && (
              <ActionsMenuItem onClick={() => onCancelForToday?.(task)} disabled={isCanceledToday || isPassedToday}>
                <span className={style.menuItemIcon} />
                <span className={style.menuItemText}>Cancel for today</span>
              </ActionsMenuItem>
            )}
          </ActionsMenu>
        )}
      </span>
    </div>
  )
}

export default FacilityCleaningTaskItem
